/**
 * LineupSet - slot-based UI with click-to-swap, auto-set, lock indicator.
 * Saves on every change (debounced).
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Zap, Lock, Check, AlertCircle } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { authHeaders } from "../contexts/AuthContext";
import SeasonShell from "../components/season/SeasonShell";
import { PlayerHeadshot } from "../components/PlayerHeadshot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LineupSet() {
  const { id: leagueId, teamId, week: weekParam } = useParams();
  const week = parseInt(weekParam, 10);
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [lineup, setLineup] = useState(null);
  const [roster, setRoster] = useState([]);
  const [pickerSlot, setPickerSlot] = useState(null);  // slot_id being edited
  const [saving, setSaving] = useState("idle");        // idle | saving | saved | error
  const [error, setError] = useState(null);
  const saveTimer = useRef(null);

  const refresh = async () => {
    setError(null);
    // Both reads are page-blocking - fail loudly, not silently.
    try {
      const [lp, rr] = await Promise.all([
        axios.get(`${API}/lineups/${teamId}/week/${week}`, { headers: authHeaders() }),
        axios.get(`${API}/teams/${teamId}/roster`, { headers: authHeaders() }),
      ]);
      setLineup(lp.data);
      setRoster(rr.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load lineup");
    }
  };

  useEffect(() => { refresh(); }, [teamId, week]);

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";
  const slotRowBg = isDark ? "bg-[#252525]" : "bg-[#F5F5F5]";

  const isLocked = lineup?.slots?.some((s) => s.is_locked);

  const usedPlayerIds = useMemo(() => {
    const s = new Set();
    for (const slot of (lineup?.slots || [])) {
      if (slot.player_id) s.add(slot.player_id);
    }
    return s;
  }, [lineup]);

  const pickerEligibility = (slot) => {
    if (!slot) return new Set();
    if (slot.slot_type === "BN") return new Set(roster.map((r) => r.player.id));
    if (slot.slot_type === "IR") {
      return new Set(roster.filter((r) => r.player.status === "injured").map((r) => r.player.id));
    }
    const elig = slot.eligible_positions || [];
    return new Set(roster.filter((r) => elig.includes(r.player.position)).map((r) => r.player.id));
  };

  const handleSwap = (slotId, newPlayerId) => {
    if (!lineup) return;
    // [HF-1 v1.1] Optimistic update must mutate BOTH `player_id` and the
    // embedded `player` object together - SlotRow reads from `slot.player`,
    // so updating only `player_id` leaves the old player rendered until a
    // refresh. Look up the new player from `roster` and swap atomically.
    const oldSlot = (lineup.slots || []).find((s) => s.slot_id === slotId);
    const old = oldSlot?.player_id || null;
    const oldPlayerObj = oldSlot?.player || null;
    const conflicting = (lineup.slots || []).find(
      (s) => s.slot_id !== slotId && s.player_id === newPlayerId
    );
    const newPlayerObj = roster.find((r) => r.player.id === newPlayerId)?.player || null;
    const next = (lineup.slots || []).map((s) => {
      if (s.slot_id === slotId) return { ...s, player_id: newPlayerId, player: newPlayerObj };
      if (conflicting && s.slot_id === conflicting.slot_id) {
        return { ...s, player_id: old, player: oldPlayerObj };
      }
      return s;
    });
    setLineup({ ...lineup, slots: next });
    setPickerSlot(null);
    debouncedSave(next);
  };

  const handleClear = (slotId) => {
    // [HF-1 v1.1] Clear both `player_id` and embedded `player` together.
    const next = (lineup.slots || []).map((s) =>
      s.slot_id === slotId ? { ...s, player_id: null, player: null } : s
    );
    setLineup({ ...lineup, slots: next });
    debouncedSave(next);
  };

  const debouncedSave = (slots) => {
    setSaving("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveAssignments(slots);
    }, 600);
  };

  const saveAssignments = async (slots) => {
    try {
      await axios.put(
        `${API}/lineups/${teamId}/week/${week}`,
        { assignments: slots.map((s) => ({ slot_id: s.slot_id, player_id: s.player_id })) },
        { headers: authHeaders() }
      );
      setSaving("saved");
      setError(null);
      setTimeout(() => setSaving((cur) => (cur === "saved" ? "idle" : cur)), 1500);
    } catch (e) {
      setSaving("error");
      setError(e?.response?.data?.detail || e.message || "Save failed");
    }
  };

  const handleAutoSet = async () => {
    try {
      setSaving("saving");
      await axios.post(`${API}/lineups/${teamId}/week/${week}/auto-set`, {}, { headers: authHeaders() });
      await refresh();
      setSaving("saved");
      setTimeout(() => setSaving("idle"), 1500);
    } catch (e) {
      setSaving("error");
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const computedTotal = useMemo(() => {
    if (!lineup) return 0;
    let t = 0;
    for (const s of lineup.slots) {
      if (!s.is_starter || !s.player) continue;
      if (s.player.bye_week === week) continue;
      t += (s.player.projected_points || 0) / 17.0;
    }
    return t;
  }, [lineup, week]);

  if (!lineup) {
    return <SeasonShell><div className="py-20 text-center"><span className="font-display font-black uppercase text-2xl text-[#FF6B00]">Loading lineup…</span></div></SeasonShell>;
  }

  const starters = lineup.slots.filter((s) => s.is_starter);
  const bench = lineup.slots.filter((s) => !s.is_starter);

  return (
    <SeasonShell>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] font-display font-bold mb-1">
            Set Lineup · Week {week}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight" data-testid="lineup-title">
            Projected: {computedTotal.toFixed(1)}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <SaveBadge saving={saving} error={error} />
          <button
            onClick={handleAutoSet}
            disabled={isLocked}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FF6B00]/15 border border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/25 text-sm font-display font-bold uppercase tracking-wide disabled:opacity-40"
            data-testid="lineup-auto-set"
          >
            <Zap size={14} /> Auto-set Optimal
          </button>
        </div>
      </div>

      {isLocked && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-yellow-500/10 border border-yellow-500/40 text-yellow-300 text-sm flex items-center gap-2">
          <Lock size={14} /> This week's lineup is locked.
        </div>
      )}

      <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-4 mb-4`} data-testid="lineup-starters">
        <div className={`text-[10px] uppercase tracking-widest font-display font-bold ${muted} mb-2`}>Starters</div>
        <div className="space-y-2">
          {starters.map((s) => (
            <SlotRow
              key={s.slot_id}
              slot={s}
              week={week}
              isDark={isDark}
              cardBg={slotRowBg}
              muted={muted}
              onPick={() => !isLocked && setPickerSlot(s.slot_id)}
              onClear={() => !isLocked && handleClear(s.slot_id)}
            />
          ))}
        </div>
      </div>

      <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-4`} data-testid="lineup-bench">
        <div className={`text-[10px] uppercase tracking-widest font-display font-bold ${muted} mb-2`}>Bench</div>
        <div className="space-y-2">
          {bench.map((s) => (
            <SlotRow
              key={s.slot_id}
              slot={s}
              week={week}
              isDark={isDark}
              cardBg={slotRowBg}
              muted={muted}
              onPick={() => !isLocked && setPickerSlot(s.slot_id)}
              onClear={() => !isLocked && handleClear(s.slot_id)}
            />
          ))}
        </div>
      </div>

      {/* Picker modal */}
      {pickerSlot && (
        <PlayerPickerModal
          isDark={isDark}
          slot={lineup.slots.find((s) => s.slot_id === pickerSlot)}
          roster={roster}
          eligibleIds={pickerEligibility(lineup.slots.find((s) => s.slot_id === pickerSlot))}
          usedIds={usedPlayerIds}
          onClose={() => setPickerSlot(null)}
          onPick={(pid) => handleSwap(pickerSlot, pid)}
        />
      )}
    </SeasonShell>
  );
}

function SaveBadge({ saving, error }) {
  if (saving === "saving") {
    return <span className="text-xs uppercase tracking-wider text-[#FF6B00] font-bold animate-pulse">Saving…</span>;
  }
  if (saving === "saved") {
    return <span className="text-xs uppercase tracking-wider text-[#00C853] font-bold flex items-center gap-1"><Check size={12} /> Saved</span>;
  }
  if (saving === "error") {
    return <span className="text-xs uppercase tracking-wider text-red-400 font-bold flex items-center gap-1" title={error}><AlertCircle size={12} /> Error</span>;
  }
  return null;
}

function StartSitBadge({ signal }) {
  // [Session C-B CP-3] Visual-only recommendation from
  // engines/start_sit.py. Backend returns "START" | "SIT" | null.
  // We render green/yellow dot + label; null → nothing. Same visual
  // vocabulary as Edge badges - small pill, uppercase, tight tracking.
  if (signal === "START") {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#00C853]/15 border border-[#00C853]/40 text-[#00C853] text-[9px] uppercase tracking-widest font-display font-black"
        data-testid="start-sit-badge-start"
        title="Favorable matchup for a strong player this week"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#00C853]" />
        Start
      </span>
    );
  }
  if (signal === "SIT") {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-yellow-500/15 border border-yellow-500/40 text-yellow-400 text-[9px] uppercase tracking-widest font-display font-black"
        data-testid="start-sit-badge-sit"
        title="Tough matchup for a bench-tier player this week"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
        Sit
      </span>
    );
  }
  return null;
}

function SlotRow({ slot, week, isDark, cardBg, muted, onPick, onClear }) {
  const p = slot.player;
  const onBye = p && p.bye_week === week;
  return (
    <div className={`flex items-center gap-3 p-2.5 rounded-lg ${cardBg}`} data-testid={`lineup-slot-${slot.slot_id}`}>
      <div className="w-12 text-center">
        <div className="text-[10px] uppercase tracking-wider text-[#FF6B00] font-display font-black">{slot.slot_id}</div>
        <div className={`text-[9px] ${muted}`}>{slot.slot_type}</div>
      </div>
      {p ? (
        <>
          <PlayerHeadshot playerId={p.id} position={p.position} size="sm" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-display font-bold uppercase text-xs tracking-wide truncate">{p.name}</div>
              <StartSitBadge signal={p.start_sit} />
            </div>
            <div className={`text-[10px] ${muted}`}>{p.position} · {p.nfl_team} {onBye && <span className="text-yellow-400 ml-1">BYE</span>}</div>
          </div>
          <div className="text-right">
            <div className={`text-[10px] uppercase ${muted}`}>Proj</div>
            <div className="font-display font-black text-sm">{onBye ? "0.0" : ((p.projected_points || 0) / 17.0).toFixed(1)}</div>
          </div>
          <button
            onClick={onPick}
            className="text-xs uppercase tracking-wider text-[#FF6B00] font-display font-bold hover:underline"
            data-testid={`lineup-swap-${slot.slot_id}`}
          >
            Swap
          </button>
        </>
      ) : (
        <button
          onClick={onPick}
          className={`flex-1 text-left text-xs italic ${muted} hover:text-[#FF6B00]`}
          data-testid={`lineup-empty-${slot.slot_id}`}
        >
          (empty - tap to fill)
        </button>
      )}
    </div>
  );
}

function PlayerPickerModal({ isDark, slot, roster, eligibleIds, usedIds, onClose, onPick }) {
  const [q, setQ] = useState("");
  const cardBg = isDark ? "bg-[#181818]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";

  const filtered = roster
    .filter((r) => eligibleIds.has(r.player.id))
    .filter((r) => !q || r.player.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.player.projected_points || 0) - (a.player.projected_points || 0));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className={`rounded-2xl ${cardBg} border ${cardBorder} max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#2A2A2A]">
          <div className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-bold">Swap into {slot.slot_id}</div>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className={`mt-2 w-full px-3 py-2 rounded-lg ${isDark ? "bg-[#252525] text-white" : "bg-[#F0F0F0] text-black"} border ${cardBorder} text-sm`}
            data-testid="picker-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {filtered.length === 0 && <div className={`text-center py-8 text-sm ${muted}`}>No eligible players.</div>}
          {filtered.map((r) => {
            const used = usedIds.has(r.player.id);
            return (
              <button
                key={r.player.id}
                onClick={() => onPick(r.player.id)}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#FF6B00]/10 transition text-left"
                data-testid={`picker-pick-${r.player.id}`}
              >
                <PlayerHeadshot playerId={r.player.id} position={r.player.position} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase text-xs tracking-wide truncate">{r.player.name}</div>
                  <div className={`text-[10px] ${muted}`}>
                    {r.player.position} · {r.player.nfl_team} · BYE {r.player.bye_week || "-"} · Proj {Math.round(r.player.projected_points || 0)}
                  </div>
                </div>
                {used && <span className="text-[9px] uppercase tracking-widest text-[#FF6B00] font-bold">In Lineup</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
